import { Dialog } from '@angular/cdk/dialog'
import {
  Component,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { Router, RouterModule } from '@angular/router'
import { MatBottomSheet } from '@angular/material/bottom-sheet'
import { MatButtonModule } from '@angular/material/button'
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
} from '@angular/material/paginator'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { firstValueFrom } from 'rxjs'

import { PaginatorIntl } from '../../../intl/paginator-intl'
import { DialogService } from '../../../../services/dialog.service'
import { ErrorService } from '../../../../services/error.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { TranslationService } from '../../../../services/translation.service'
import { ApiError } from '../../../../types/apiError'
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../shared/confirm-dialog/confirm-dialog.component'
import { CommonService } from '../../../../services/common.service'
import { BulkProductShareCsvTemplate } from '../../../../types/productSharingCsvTemplates'
import { ProductSharingFieldMappingSheetComponent } from './product-sharing-field-mapping-sheet/product-sharing-field-mapping-sheet.component'
import { ProductSharingCsvTemplateService } from '../../../../services/productSharingCsvTemplate.service'

@Component({
  selector: 'app-product-sharing-template-list',
  imports: [MatButtonModule, MatTableModule, MatPaginatorModule, RouterModule],
  providers: [
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorIntl,
    },
  ],
  templateUrl: './product-sharing-template-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './product-sharing-template-list.component.scss',
})
export class ProductSharingTemplateListComponent implements OnInit {
  translationService = inject(TranslationService)
  private dialog = inject(Dialog)
  private dialogService = inject(DialogService)
  private errorService = inject(ErrorService)
  private spinnerService = inject(SpinnerService)
  private csvTemplateService = inject(ProductSharingCsvTemplateService)
  private bottomSheet = inject(MatBottomSheet)
  private router = inject(Router)
  private commonService = inject(CommonService)

  csvTemplates: Array<BulkProductShareCsvTemplate> = []
  displayedColumns: Array<string> = ['name', 'actions']
  dataSource = new MatTableDataSource<BulkProductShareCsvTemplate>([])

  @ViewChild(MatPaginator) set paginator(mp: MatPaginator) {
    this.dataSource.paginator = mp
  }

  async ngOnInit(): Promise<void> {
    await this.getResults()
  }

  /**
   * Retrieves templates for the authenticated user's company.
   * @returns {Promise<void>}
   */
  async getResults(): Promise<void> {
    try {
      this.spinnerService.startSpinner()
      const response = await firstValueFrom(this.csvTemplateService.getAll())
      this.csvTemplates = response
      this.dataSource.data = this.csvTemplates
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Opens bottom sheet to show CSV field mappings.
   * @param {BulkProductShareCsvTemplate} csvTemplate Template to display.
   * @returns {void}
   */
  showFieldMappings(csvTemplate: BulkProductShareCsvTemplate): void {
    this.bottomSheet.open(ProductSharingFieldMappingSheetComponent, {
      data: {
        csvTemplate,
      },
    })
  }

  /**
   * Navigates user to edit template screen.
   * @param {string | undefined} id The id of the template to edit.
   * @returns {void}
   */
  goToEdit(id: string | undefined): void {
    if (!id) return
    this.router.navigate([
      'settings',
      'product-sharing-csv-templates',
      id,
      'edit',
    ])
  }

  /**
   * Prompts user to delete a CSV template and updates the table on success.
   * @param {string | undefined} id Id of the template.
   * @param {string} name Name of the template.
   * @returns {Promise<void>}
   */
  async delete(id: string | undefined, name: string): Promise<void> {
    if (!id) return

    try {
      const dialogData: ConfirmDialogData = {
        header: this.translationService.getConfirmActionHeaderText(),
        message: this.translationService.getDeleteTemplateMessageText(name),
        onCancel: () => {},
      }

      const dialogRef = this.dialog.open<boolean>(ConfirmDialogComponent, {
        data: dialogData,
      })

      dialogRef.closed.subscribe(async (confirmed) => {
        if (!confirmed) return

        try {
          this.spinnerService.startSpinner()
          await firstValueFrom(this.csvTemplateService.delete(id))
          this.spinnerService.stopSpinner()

          const index = this.csvTemplates.findIndex((t) => t.id === id)
          if (index !== -1) {
            this.csvTemplates.splice(index, 1)
            this.dataSource.data = [...this.csvTemplates]
          }

          this.dialogService.showDialog(
            this.dialog,
            this.translationService.getProductSharingCSVTemplatesText(),
            this.translationService.getTemplateDeletedText(),
          )
        } catch (err) {
          this.spinnerService.stopSpinner()
          const apiErr = err as ApiError
          this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
        }
      })
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Downloads a CSV template.
   * @param {string | undefined} id Id of the template.
   * @param {string} name Name of the template.
   * @returns {Promise<void>}
   */
  async download(id: string | undefined, name: string): Promise<void> {
    if (!id) return

    try {
      const fileName = name + '.csv'
      this.spinnerService.startSpinner()
      const base64 = await firstValueFrom(this.csvTemplateService.download(id))
      this.commonService.downloadBase64File(base64, fileName)
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }
}
